import {CssPathStep, EAlertType, IEvent, IEventForm, IField, ILogLevels, IRoute, IStyleAttributes, IStylePosition, TWindowType} from './ObjectTypes';

/**
 * Common observer methods
 */
export default class ObserverCommon {
  static field_type_map = {
    button: 'Button',
    cell: 'Cell',
    gridcell: 'Cell',
    rowheader: 'Cell',
    columnheader: 'Cell',
    checkbox: 'Checkbox',
    checklist: 'Checklist',
    column: 'Column',
    combobox: 'Inputbox',
    spinbutton: 'Inputbox',
    dialog: 'Dialog',
    dialogbutton: 'DialogButton',
    dropdown: 'DropdownList',
    dropdownitem: 'Item',
    dropdownlist: 'DropdownList',
    frame: 'Frame',
    image: 'Image',
    input: 'InputBox',
    inputbox: 'InputBox',
    item: 'Item',
    label: 'Label',
    link: 'Link',
    list: 'ListBox',
    listbox: 'ListBox',
    listitem: 'Item',
    menu: 'Menu',
    menuitem: 'Item',
    object: 'Object',
    page: 'Page',
    radio: 'RadioButton',
    radiobutton: 'RadioButton',
    radiogroup: 'RadioGroup',
    region: 'Region',
    section: 'Region',
    row: 'Row',
    scrollbar: 'Scrollbar',
    slider: 'Slider',
    tab: 'Tab',
    table: 'Table',
    grid: 'Table',
    tabpanel: 'Text',
    text: 'Text',
    textarea: 'Textarea',
    tree: 'Tree',
    treeitem: 'Item',
    unknown: 'Unknown',
    widget: 'Widget',
    window: 'Window',
  };

  static valid_roles = [
    'checklistitem',
    'dropdownitem',
    'dropdownlist',
    'columnheader',
    'radiobutton',
    'radiogroup',
    'navigation',
    'spinbutton',
    'rowheader',
    'checklist',
    'dropdown',
    'textarea',
    'treeitem',
    'treelist',
    'inputbox',
    'checkbox',
    'listitem',
    'menuitem',
    'combobox',
    'gridcell',
    'listbox',
    'region',
    'slider',
    'widget',
    'dialog',
    'button',
    'option',
    'select',
    'switch',
    'radio',
    'label',
    'panel',
    'input',
    'image',
    'table',
    'page',
    'item',
    'cell',
    'link',
    'list',
    'menu',
    'tree',
    'grid',
    'nav',
    'row',
    'tab',
    'text',
  ];

  static tag_roles = {
    textarea: 'textarea',
    section: 'region',
    select: 'dropdown',
    button: 'button',
    picture: 'image',
    legend: 'label',
    label: 'label',
    table: 'table',
    img: 'image',
    nav: 'nav',
    li: 'listitem',
    ol: 'list',
    ul: 'list',
    td: 'cell',
    th: 'cell',
    tr: 'row',
    a: 'link',
  };

  static press_keys = [
    'BACKSPACE',
    'CAPSLOCK',
    'DELETE',
    'LEFT',
    'DOWN',
    'ENTER',
    'ESCAPE',
    'F5',
    'TAB',
    'SPACE',
    'ARROWUP',
    'ARROWDOWN',
    'ARROWRIGHT',
    'ARROWLEFT',
  ];

  static change_roles = [
    'checklistitem',
    'dropdownlist',
    'radiobutton',
    'spinbutton',
    'checklist',
    'textarea',
    'dropdown',
    'checkbox',
    'inputbox',
    'combobox',
    'listbox',
    'textbox',
    'select',
    'option',
    'input',
    'check',
    'radio',
  ];

  static click_roles = [
    'checklistitem',
    'dropdownitem',
    'radiobutton',
    'checkbox',
    'listitem',
    'menuitem',
    'treeitem',
    'gridcell',
    'button',
    'option',
    'switch',
    'radio',
    'check',
    'menu',
    'item',
    'cell',
    'link',
    'row',
    'tab',
  ];

  static expand_roles = [
    'panel',
    'widget',
    'region',
    'section',
  ];

  static table_roles = [
    'table',
    'grid',
    'row',
    'rowgroup',
    'cell',
    'gridcell',
    'rowheader',
    'columnheader',
  ];


  /**
   * Determine if the current event is a duplicate of the last
   */
  static isDuplicateEvent(event?: IEvent, lastEvent?:IEvent): boolean {
    const field = event
      ? event.fields.find((f) => f.fid === event.fid)
      : undefined;
    const lastField = lastEvent
      ? lastEvent.fields.find((f) => f.fid === lastEvent.fid)
      : undefined;

    if (
      event &&
      event.event !== 'SendKey' &&
      lastEvent &&
      lastEvent.event === event.event &&
      field &&
      lastField &&
      field.type !== 'checklist' &&
      this.compareEventForms(event.form, lastEvent.form)
    ) {
      // same field and attributes
      if (field.fid === lastField.fid) {
        if (event.event === 'Click') {
          if (event.time - lastEvent.time <= 800) {
            return true;
          }
        } else if (!event.validate && !lastEvent.validate) {
          return true;
        } else if (event.validate && lastEvent.validate) {
          const matched = this.compareProperties(event.validate, lastEvent.validate);
          if (matched) {
            return matched;
          }
        }
      }

      if (lastField.type === field.type && lastField.selectors.xpath === field.selectors.xpath && lastField.selectors.shadowXpath === field.selectors.shadowXpath) {
        if (event.event === 'Set' || event.event === 'Type') {
          return true;
        }

        if (event.event === 'Change' && lastField.attributes.value === field.attributes.value) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Compare first level properties
   */
  static compareProperties(object1: any, object2: any) {
    let matched = true;

    for (const key in object1) {
      if (object1[key] !== object2[key]) {
        matched = false;
        break;
      }
    }

    return matched;
  }

  /**
   * Compares forms
   */
  static compareEventForms(form1?: IEventForm, form2?: IEventForm): boolean {
    if (!form1 || !form2) {
      return false;
    }

    if (form1.id && form2.id && !this.compareValues(form1.id, form2.id)) {
      return false;
    }

    if (!this.compareValues(form1.name, form2.name)) {
      return false;
    }

    return true;
  }

  /**
   * Get the IField object for the specified event
   */
  static getEventField(event?: IEvent, fid?: string): IField | undefined {
    if (!event) return;
    if (!event.fid && !fid) return;
    return event.fields.find((f) => f.fid === (fid || event.fid));
  }

  /**
   * Get the display name of the field
   */
  static getFieldName(label?: string, group?: string) {
    if (!label) {
      return 'Unknown';
    }

    const fullName = group && !this.compareValues(group, label)
      ? ' - ' + group
      : '';

    return label + fullName;
  }

  /**
   * Truncates the text to the specified limit
   */
  static truncateText(text: string, length: number, normalize?: boolean) {
    const s = text && normalize
      ? text.replace(/\s+/gm, ' ')
      : text;

    return s && s.length > length
      ? s.substr(0, length-1) + '...'
      : s;
  }

  /**
   * To proper case
   */
  static toProperCase(str: string) {
    return str.replace(/(^|\s)\S/g, (t: string) => {
      return t.toUpperCase();
    });
  }

  /**
   * Gets the domain of the current URL
   */
  static getUrlDomain(url?: string):string {
    if (!url) {
      return '';
    }

    const _hostname = new URL(url).hostname;
    if (_hostname === 'localhost') {
      return _hostname;
    }

    const parts = _hostname.split('.');
    if (parts.length < 2) {
      return _hostname;
    }

    return parts[parts.length - 2] + '.' + parts[parts.length - 1];
  }

  /**
   * Validates that the protocol is http or https
   */
  static isValidProtocol(urlString?: string) {
    if (!urlString) return false;
    const url = new URL(urlString);
    return ['https:', 'http:'].includes(url.protocol);
  }

  /**
   * Clones all properties of the specified object into the specified clone
   */
  static deepClone(clone: any, obj: any): any {
    for (const i in obj) {
      clone[i] = (obj[i] && typeof obj[i] == 'object')
        ? this.deepClone(obj[i].constructor(), obj[i])
        : obj[i];
    }
    return clone;
  }

  /**
   * Matches the specified url against all route patterns
   */
  static matchUrl(routes:IRoute[], url?:string):IRoute[] {
    if (!routes || !routes.length || !url) {
      return [];
    }

    const enabled = routes.filter((r) => r.enabled);
    if (!enabled || !enabled.length) {
      return [];
    }

    return enabled.filter((r) => RegExp(r.pattern, 'gi').test(url));
  }

  /**
   * Calculates a hash of the specified text
   */
  static getHash(text: string): string {
    if (!text) return '';

    let hash = 0;
    let chr: number;
    for (let i = 0; i < text.length; i++) {
      chr = text.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0;
    }

    return hash.toString();
  }

  /**
   * Compares values ignoring case and spaces
   */
  static compareValues(value1?: string, value2?: string): boolean {
    const v1 = (value1 ?? '').toString().toLowerCase().replace(/\s+/gm, ' ');
    const v2 = (value2 ?? '').toString().toLowerCase().replace(/\s+/gm, ' ');
    return v1 === v2;
  }

  /**
   * Gets the type of window
   */
  static getWindowType(): TWindowType {
    if (window.self === window.top && !window.opener) {
      return 'window';
    }

    if (window.opener) {
      return 'modal';
    }

    return 'frame';
  }

  /**
   * Returns the ordinal of the specified number
   */
  static ordinalSuffix(location?: number, total?: number): string {
    if (!location || !total) {
      return '';
    }

    if (location === 1 && total < 2) {
      return '';
    }

    const j = location % 10;
    const k = location % 100;

    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  /**
   * Checks the elements visibility and size
   */
  static isDisplayed(el: any, styleCallback?: Function): boolean {
    if (!el || !el.getAttribute) {
      return false;
    }

    const ariaLive = el.getAttribute('aria-live');
    if (ariaLive === 'polite' || ariaLive === 'assertive') {
      return true;
    }

    let _displayed = !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    if (!_displayed) {
      return false;
    }

    const _rect = el.getBoundingClientRect();
    if (_displayed = el.offsetWidth + el.offsetHeight + _rect.height + _rect.width === 0) {
      return false;
    }

    const style = getComputedStyle(el);
    if (typeof styleCallback === 'function') {
      styleCallback(style);
    }

    if (style.display === 'none') {
      return false;
    }

    if (style.visibility !== 'visible') {
      return false;
    }

    if (style.opacity && +style.opacity < 0.1) {
      return false;
    }

    return true;
  }

  /**
   *
   */
  static isNullOrUndefined(parameter: any): boolean {
    return parameter === null || parameter === undefined;
  }

  /**
   *
   */
  static getElementInfo(el: HTMLElement, frames?: boolean): IStyleAttributes {
    const result: IStyleAttributes = {
      'border-top-width': 0,
      'border-right-width': 0,
      'border-bottom-width': 0,
      'border-left-width': 0,
      'margin-top': 0,
      'margin-right': 0,
      'margin-bottom': 0,
      'margin-left': 0,
      'padding-top': 0,
      'padding-right': 0,
      'padding-bottom': 0,
      'padding-left': 0,
      'z-index': 0,
      'width': 0,
      'height': 0,
      'top': 0,
      'left': 0
    };
    const computedStyle = getComputedStyle(el);

    for (const key in result) {
      result[key] = Number.parseFloat(computedStyle[key]) || 0;
    }

    result.width = el.offsetWidth - result['border-left-width'] - result['border-right-width'] - result['padding-left'] - result['padding-right'];
    result.height = el.offsetHeight - result['border-top-width'] - result['border-bottom-width'] - result['padding-top'] - result['padding-bottom'];

    const position = this.findPos(el, frames);
    result.top = position.top;
    result.left = position.left;

    return result;
  }

  /**
   *
   */
  static findPos(ele: HTMLElement, frames?: boolean): IStylePosition {
    let computedStyle = getComputedStyle(ele);
    let rect = ele.getBoundingClientRect();
    let _x = rect.left - Number.parseFloat(computedStyle['margin-left']);
    let _y = rect.top - Number.parseFloat(computedStyle['margin-top']);

    if (frames) {
      let frm = frameElement;

      while (frm) {
        computedStyle = getComputedStyle(frm);
        rect = frm.getBoundingClientRect();
        _x += rect.left - Number.parseFloat(computedStyle['margin-left']);
        _y += rect.top - Number.parseFloat(computedStyle['margin-top']);
        const doc = frm.ownerDocument as any;
        const win = doc?.defaultView || doc?.parentWindow;
        frm = win?.frameElement;
      }
    }

    return {
      top: _y,
      left: _x
    };
  }

  /**
  * Wraps console.log with formatted messages
  */
  static consoleData(logging: boolean, version: string, label: any, level: EAlertType, levels?: ILogLevels, data?: { [k: string]: any }) {
    if (!logging) {
      return;
    }

    if (level === EAlertType.info && !levels?.info) {
      return;
    }

    if (level === EAlertType.verbose && !levels?.verbose) {
      return;
    }

    if (level === EAlertType.warn && !levels?.warn) {
      return;
    }

    if (level === EAlertType.error && !levels?.error) {
      return;
    }

    const log: { [k: string]: any } = data ?? {};

    if (levels?.verbose) {
      log.location = window.location.href;
    }

    let _label = '';
    if (label) {
      _label = `: ${label}`;
    }

    if (window.navigator.userAgent.match(/MSIE|Trident/) === null) {
      let color: string;
      switch (level) {
        case EAlertType.error:
          color = 'red';
          break;
        case EAlertType.warn:
          color = '#EAA800';
          break;
        case EAlertType.success:
          color = '#659E0B';
          break;
        default:
          color = '#0E6397';
          break;
      }

      console.log(`%c[Observer ${version}${_label}]`, `color:${color};font-weight:bold;`, log);
    } else {
      console.log(`[Observer ${version}${_label}]`, log);
    }
  }

  static getCssPath (node: any, optimized: boolean): string {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const steps: CssPathStep[] = [];
    let contextNode = node;

    while (contextNode) {
      const step = this.cssPathStep(contextNode, optimized, contextNode === node);
      if (!step) {
        break;
      }

      steps.push(step);
      if (step.optimized) {
        break;
      }

      contextNode = contextNode.parentNode;
    }
    steps.reverse();
    return steps.map(s => s.value).join(' > ');
  }

  static cssPathStep(node: any, optimized: boolean, isTargetNode: boolean): CssPathStep | null {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const id = node.getAttribute('id');
    if (optimized) {
      if (id) {
        return {
          value: this.idSelector(id),
          optimized: true
        };
      }
      const nodeNameLower = node.nodeName.toLowerCase();
      if (nodeNameLower === 'body' || nodeNameLower === 'head' || nodeNameLower === 'html') {
        return {
          value: node.tagName.toLowerCase(),
          optimized: true
        };
      }
    }

    const nodeName = node.tagName.toLowerCase();
    if (id) {
      return {
        value: nodeName + this.idSelector(id),
        optimized: true
      };
    }

    const parent = node.parentNode;
    if (!parent || parent.nodeType === Node.DOCUMENT_NODE) {
      return {
        value: nodeName,
        optimized: true
      };
    }

    const prefixedOwnClassNamesArray = this.prefixedElementClassNames(node);
    let needsClassNames = false;
    let needsNthChild = false;
    let ownIndex = -1;
    let elementIndex = -1;
    const siblings = parent.children;

    for (let i = 0; (ownIndex === -1 || !needsNthChild) && i < siblings.length; ++i) {
      const sibling = siblings[i];
      if (sibling.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }
      elementIndex += 1;
      if (sibling === node) {
        ownIndex = elementIndex;
        continue;
      }
      if (needsNthChild) {
        continue;
      }
      if (sibling.tagName.toLowerCase() !== nodeName) {
        continue;
      }

      needsClassNames = true;
      const ownClassNames = prefixedOwnClassNamesArray.values();
      let ownClassNameCount = 0;

      // eslint-disable-next-line no-unused-vars
      for (const name in ownClassNames) {
        ++ownClassNameCount;
      }

      if (ownClassNameCount === 0) {
        needsNthChild = true;
        continue;
      }

      const siblingClassNamesArray = this.prefixedElementClassNames(sibling);

      for (let j = 0; j < siblingClassNamesArray.length; ++j) {
        const siblingClass = siblingClassNamesArray[j];
        if (!ownClassNames.hasOwnProperty(siblingClass)) {
          continue;
        }
        delete ownClassNames[siblingClass];
        if (!--ownClassNameCount) {
          needsNthChild = true;
          break;
        }
      }
    }

    let result = nodeName;

    if (isTargetNode && nodeName.toLowerCase() === 'input' && node.getAttribute('type') && !node.getAttribute('id') && !node.getAttribute('class')) {
      result += `[type="${node.getAttribute('type')}"]`;
    }

    if (needsNthChild) {
      result += `:nth-child(${ownIndex + 1})`;
    } else if (needsClassNames) {
      for (const prefixedName in prefixedOwnClassNamesArray.values()) {
        result += `.${this.escapeIdentifierIfNeeded(prefixedName.substr(1))}`;
      }
    }

    return {
      value: result,
      optimized: false
    };
  }

  static prefixedElementClassNames(node: any) {
    const classAttribute = node.getAttribute('class');
    if (!classAttribute) {
      return [];
    }
    return classAttribute.split(/\s+/g).filter(Boolean).map(function (name) {
      return `$${name}`;
    });
  }

  static idSelector(id: string) {
    return `#${this.escapeIdentifierIfNeeded(id)}`;
  }

  static escapeIdentifierIfNeeded(ident: string) {
    if (this.isCSSIdentifier(ident)) {
      return ident;
    }
    const shouldEscapeFirst = /^(?:[0-9]|-[0-9-]?)/.test(ident);
    const lastIndex = ident.length - 1;
    return ident.replace(/./g, function (c, i) {
      return ((shouldEscapeFirst && i === 0) || !this.isCSSIdentChar(c))
        ? this.escapeAsciiChar(c, i === lastIndex)
        : c;
    });
  }

  static escapeAsciiChar(c: string, isLast: boolean) {
    return `\\${this.toHexByte(c)}${isLast ? '' : ' '}`;
  }

  static toHexByte(c: string) {
    const hexByte = c.charCodeAt(0).toString(16);

    if (hexByte.length === 1) {
      return `0${hexByte}`;
    }

    return hexByte;
  }

  static isCSSIdentChar(c: string) {
    if (/[a-zA-Z0-9_-]/.test(c)) {
      return true;
    }
    return c.charCodeAt(0) >= 0xA0;
  }

  static isCSSIdentifier(value: string) {
    return /^-?[a-zA-Z_][a-zA-Z0-9_-]*$/.test(value);
  }
}
